import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useReducer } from 'react';
import { JobSchema } from 'lib/sdks/jobs-management/job.schema';
import type { Job, PartialJob } from 'lib/sdks/jobs-management/job.type';
import { getJobDescription } from './utils/job-description';

export type JobContextState = {
  type: 'RequestAQuote' | 'DirectMessage';
} & PartialJob;

type SetJobField = <
  FieldKey extends keyof Job,
  Value extends JobContextState[FieldKey],
>(
  field: FieldKey,
  value: Value,
) => void;

type JobReducer = <
  JobKey extends keyof Job,
  JobValue extends JobContextState[JobKey],
>(
  state: JobContextState,
  action: {
    type: 'update-field';
    field: JobKey;
    value: JobValue;
  },
) => JobContextState;

const JobContext = createContext<{
  job: JobContextState;
  dispatch: Dispatch<Parameters<JobReducer>[1]>;
} | null>(null);

export const JobProvider = JobContext.Provider;

const jobReducer: JobReducer = (state, action) => {
  if (action.type === 'update-field') {
    return { ...state, [action.field]: action.value };
  }

  return state;
};

export const JobContextProvider = ({
  initialState = { type: 'RequestAQuote' },
  children,
}: {
  initialState?: JobContextState;
  children: ReactNode;
}) => {
  const [job, dispatch] = useReducer(jobReducer, {
    additionalData: [],
    leads: [],
    description: getJobDescription(),
    ...initialState,
  });

  const jobContext = { job, dispatch };

  return <JobProvider value={jobContext}>{children}</JobProvider>;
};

export const useJob = () => {
  const jobContext = useContext(JobContext);

  if (jobContext === null) {
    throw new Error('useJob hook should be used inside of JobProvider');
  }

  const { job, dispatch } = jobContext;

  const getJob = () => {
    return JobSchema.parse(job);
  };

  const setJobField: SetJobField = (field, value) => {
    dispatch({ type: 'update-field', field, value });
  };

  return { job, setJobField, getJob };
};
